import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toPng } from "html-to-image";
import { X, Download, Share2, Trophy, Award } from "lucide-react";
import { toast } from "sonner";

const BELT_FILL = {
  White: "#F8FAFC",
  Blue: "#1D4ED8",
  Purple: "#6B21A8",
  Brown: "#78350F",
  Black: "#000000",
};

const OVERLAY_INITIAL = { opacity: 0 };
const OVERLAY_ANIMATE = { opacity: 1 };
const OVERLAY_EXIT = { opacity: 0 };
const CARD_INITIAL = { opacity: 0, scale: 0.92, y: 20 };
const CARD_ANIMATE = { opacity: 1, scale: 1, y: 0 };
const CARD_EXIT = { opacity: 0, scale: 0.95 };
const SPRING = { type: "spring", stiffness: 300, damping: 28 };

export default function PromotionCard({ open, onClose, belt, userName, skills }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const dateStr = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  async function handleDownload() {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#0A0A0A",
        skipFonts: true,
      });
      const link = document.createElement("a");
      link.download = `tatami-log-${belt.toLowerCase()}-belt-promotion.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Promotion card downloaded");
    } catch (err) {
      console.error("Download failed", err);
      toast.error("Couldn't generate image");
    } finally {
      setBusy(false);
    }
  }

  function handleShareX() {
    const text = `Just locked in my ${belt} belt skill roadmap on @TatamiLog 🥋 ${skills.length} core techniques documented. White belt → black belt, one roll at a time.`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  async function handleCopyShareText() {
    const text = `Just locked in my ${belt} belt skill roadmap on Tatami Log 🥋\n${skills.length} core techniques documented.\nWhite belt → black belt, one roll at a time.`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Caption copied — paste it on Instagram with your downloaded card");
    } catch (err) {
      console.error("Clipboard write failed", err);
      toast.error("Couldn't copy text");
    }
  }

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={OVERLAY_INITIAL}
        animate={OVERLAY_ANIMATE}
        exit={OVERLAY_EXIT}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 overflow-y-auto"
        onClick={onClose}
        data-testid="promotion-card-overlay"
      >
        <motion.div
          initial={CARD_INITIAL}
          animate={CARD_ANIMATE}
          exit={CARD_EXIT}
          transition={SPRING}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-lg my-8"
        >
          <button
            onClick={onClose}
            data-testid="promotion-card-close"
            className="absolute -top-3 -right-3 z-10 w-10 h-10 bg-red-600 hover:bg-red-700 flex items-center justify-center"
          >
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>

          {/* The actual shareable card */}
          <div
            ref={cardRef}
            data-testid="promotion-card-image"
            className="bg-[#0A0A0A] border border-zinc-800 p-8 relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 right-0 h-1 bg-red-600" />
            <div className="absolute inset-0 bg-gradient-to-br from-red-600/5 to-transparent pointer-events-none" />

            <div className="relative">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-red-600 flex items-center justify-center">
                    <Trophy className="w-4 h-4" strokeWidth={2.5} />
                  </div>
                  <span className="font-heading text-lg">TATAMI LOG</span>
                </div>
                <span className="font-mono text-[10px] text-zinc-500 tracking-widest">
                  PROMOTION CARD
                </span>
              </div>

              <p className="font-label text-red-600 mb-2">Roadmap Complete</p>
              <h2 className="font-heading text-4xl md:text-5xl leading-[0.95] mb-1">
                {userName || "Grappler"}
              </h2>
              <p className="font-heading text-3xl md:text-4xl text-red-600 leading-tight mb-6">
                hits 100%.
              </p>

              {/* Belt visual */}
              <div className="my-6 border border-zinc-800">
                <div
                  className="h-8 relative"
                  style={{
                    background: BELT_FILL[belt] || "#F8FAFC",
                    borderBottom: belt === "White" ? "1px solid #d4d4d8" : "none",
                  }}
                >
                  <div className="absolute right-[8%] top-0 bottom-0 w-1.5 bg-red-700" />
                  <div className="absolute right-0 top-0 bottom-0 w-[12%] bg-black border-l border-zinc-700" />
                </div>
                <div className="bg-[#141414] px-3 py-2 flex items-center justify-between">
                  <span className="font-heading text-sm tracking-wider">{belt} BELT</span>
                  <span className="font-mono text-[10px] text-zinc-500">{dateStr}</span>
                </div>
              </div>

              {/* Skills */}
              <p className="font-label mb-3">Techniques Documented</p>
              <div className="grid grid-cols-2 gap-1.5">
                {skills.map((skill) => (
                  <div
                    key={skill}
                    className="border-l-2 border-l-red-600 bg-[#141414] px-2.5 py-1.5"
                  >
                    <span className="text-xs text-white">{skill}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-4 border-t border-zinc-800 flex items-center justify-between">
                <span className="font-mono text-[10px] text-zinc-600">
                  TRAIN HARD. TAP OFTEN. LEARN ALWAYS.
                </span>
                <Award className="w-4 h-4 text-red-600" strokeWidth={2.5} />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-4 grid grid-cols-3 gap-2">
            <button
              onClick={handleDownload}
              disabled={busy}
              data-testid="promotion-download-button"
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-heading tracking-wider py-3 flex items-center justify-center gap-2 text-sm"
            >
              <Download className="w-4 h-4" strokeWidth={2.5} />
              PNG
            </button>
            <button
              onClick={handleShareX}
              data-testid="promotion-share-x-button"
              className="border border-zinc-700 hover:border-red-600 hover:text-red-500 font-heading tracking-wider py-3 flex items-center justify-center gap-2 text-sm transition-colors"
            >
              <Share2 className="w-4 h-4" strokeWidth={2.5} />
              X / TWITTER
            </button>
            <button
              onClick={handleCopyShareText}
              data-testid="promotion-copy-button"
              className="border border-zinc-700 hover:border-red-600 hover:text-red-500 font-heading tracking-wider py-3 flex items-center justify-center gap-2 text-sm transition-colors"
            >
              <Share2 className="w-4 h-4" strokeWidth={2.5} />
              IG CAPTION
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
